/**
 * "Works at" with a starred primary (Users → person → Employment, bulk edit, invitations, CSV import). The data model is
 * unchanged: `employee_locations` is the list and `employees.primary_location_id` the star. The rule everywhere: the
 * primary is always one of the places someone works at, and anyone with at least one place has exactly one primary.
 */
export interface WorksAt { ids: string[]; primary: string | null }

/** Keep the rule: no duplicates; a missing or stray primary falls back to the first place; none selected, no primary. */
export function normaliseWorksAt(v: WorksAt): WorksAt {
  const ids = [...new Set(v.ids)];
  const primary = v.primary && ids.includes(v.primary) ? v.primary : ids[0] ?? null;
  return { ids, primary };
}

export const sameWorksAt = (a: WorksAt, b: WorksAt) => a.primary === b.primary && [...a.ids].sort().join() === [...b.ids].sort().join();

/** Remove a place. If it was the primary, the next place (in the order shown) becomes primary. */
export function removeWorksAt(v: WorksAt, id: string): { value: WorksAt; promoted: string | null } {
  const i = v.ids.indexOf(id);
  const ids = v.ids.filter(x => x !== id);
  if (v.primary !== id) return { value: normaliseWorksAt({ ids, primary: v.primary }), promoted: null };
  const next = ids[i] ?? ids[i - 1] ?? ids[0] ?? null;
  return { value: { ids, primary: next }, promoted: next };
}

/**
 * Toggle the star. Starring makes it the primary. Unstarring the primary hands the star to the next place; with only
 * one place there is nothing to hand it to, so it stays (one primary is required).
 */
export function toggleWorksAtStar(v: WorksAt, id: string): { value: WorksAt; promoted: string | null; kept: boolean } {
  if (v.primary !== id) return { value: normaliseWorksAt({ ids: v.ids, primary: id }), promoted: null, kept: false };
  const others = v.ids.filter(x => x !== id);
  if (!others.length) return { value: v, promoted: null, kept: true };
  const i = v.ids.indexOf(id);
  const next = v.ids[i + 1] ?? others[0];
  return { value: { ids: v.ids, primary: next }, promoted: next, kept: false };
}

/** Invitations store one list; the first entry is the primary. */
export const worksAtToList = (v: WorksAt): string[] => { const n = normaliseWorksAt(v); return n.primary ? [n.primary, ...n.ids.filter(x => x !== n.primary)] : n.ids; };
export const listToWorksAt = (ids: string[]): WorksAt => normaliseWorksAt({ ids, primary: ids[0] ?? null });

/**
 * Per-person result of a bulk "Works at" action (null = nothing to change). Add: adds the places, and the starred one
 * (if any) becomes everyone's primary. Remove: removes them; a removed primary passes to the next remaining place.
 */
export function bulkWorksAtPatch(e: { location_ids: string[]; primary_location_id: string | null }, ids: string[], action: 'add' | 'remove', star: string | null = null) {
  const cur = normaliseWorksAt({ ids: e.location_ids, primary: e.primary_location_id });
  let next: WorksAt;
  if (action === 'add') next = normaliseWorksAt({ ids: [...cur.ids, ...ids, ...(star ? [star] : [])], primary: star ?? cur.primary });
  else { next = cur; for (const id of ids) next = removeWorksAt(next, id).value; }
  if (sameWorksAt(next, { ids: e.location_ids, primary: e.primary_location_id })) return null;
  return { location_ids: next.ids, primary_location_id: next.primary };
}
